// Memory-budget eviction for streamed photogrammetry tiles, independent of the LOD strategy.
//
// Tiles stay loaded after they stop being drawn ("hidden", not "unloaded"), so turning back or
// zooming out redraws them without a download. Only when the cache exceeds its budget are tiles
// released, least useful first:
//   1. tiles the current camera position would not draw even if it looked at them (their need is
//      below 1, for example far tiles finer than the screen can show), least needed first;
//   2. then the least recently used tiles, least needed first among equals.
// Tiles used in the current frame and pinned tiles (the coarsest levels, so there is always
// something to draw) are never released.

export interface HsTileCacheEntry {
  /** GPU and CPU bytes the tile holds while loaded. */
  bytes: number;
  /** Frame in which the tile was last drawn or needed for drawing. */
  lastUsed: number;
  /**
   * How much the current camera position needs this tile: its parent's screen error divided by
   * the error threshold. Below 1 the tile would not be drawn from here in any direction.
   */
  need: number;
  pinned: boolean;
}

/** Returns the tiles to release, in release order, to bring `totalBytes` within `budgetBytes`. */
export function hsChooseTileEvictions<T extends HsTileCacheEntry>(
  resident: readonly T[],
  totalBytes: number,
  budgetBytes: number,
  frame: number
): T[] {
  if (totalBytes <= budgetBytes) {
    return [];
  }
  const candidates: T[] = resident
    .filter((entry: T) => !entry.pinned && entry.lastUsed < frame)
    .sort((a: T, b: T) => {
      const aUnneeded: boolean = a.need < 1;
      const bUnneeded: boolean = b.need < 1;
      if (aUnneeded !== bUnneeded) {
        return aUnneeded ? -1 : 1;
      }
      if (!aUnneeded && a.lastUsed !== b.lastUsed) {
        return a.lastUsed - b.lastUsed;
      }
      return a.need - b.need;
    });
  const evicted: T[] = [];
  let remaining: number = totalBytes;
  for (const entry of candidates) {
    if (remaining <= budgetBytes) {
      break;
    }
    remaining -= entry.bytes;
    evicted.push(entry);
  }
  return evicted;
}

/** What the device budgets look at: touch devices with a coarse pointer and low-memory devices get small budgets. */
export interface HsDeviceEnvironment {
  maxTouchPoints?: number;
  deviceMemory?: number;
  coarsePointer?: boolean;
}

/** True on phones and tablets (touch with a coarse pointer) and on devices with at most 4 GB of memory. */
export function hsIsConstrainedDevice(environment: HsDeviceEnvironment = hsReadDeviceEnvironment()): boolean {
  const mobile: boolean = (environment.maxTouchPoints ?? 0) > 0 && environment.coarsePointer === true;
  const lowMemory: boolean = environment.deviceMemory !== undefined && environment.deviceMemory <= 4;
  return mobile || lowMemory;
}

/** Default cache budget: smaller on phones and tablets, where WebGL contexts are lost much sooner. */
export function hsDefaultTileCacheBudgetMiB(environment: HsDeviceEnvironment = hsReadDeviceEnvironment()): number {
  return hsIsConstrainedDevice(environment) ? HS_MOBILE_TILE_CACHE_BUDGET_MIB : HS_DESKTOP_TILE_CACHE_BUDGET_MIB;
}

export const HS_DESKTOP_TILE_CACHE_BUDGET_MIB: number = 2048;
export const HS_MOBILE_TILE_CACHE_BUDGET_MIB: number = 256;

/** The device signals of the current browser, or none outside a browser. */
export function hsReadDeviceEnvironment(): HsDeviceEnvironment {
  const scope: { navigator?: Navigator & { deviceMemory?: number }; matchMedia?: (query: string) => MediaQueryList } =
    globalThis as typeof globalThis & { navigator?: Navigator & { deviceMemory?: number } };
  return {
    maxTouchPoints: scope.navigator?.maxTouchPoints,
    deviceMemory: scope.navigator?.deviceMemory,
    coarsePointer: scope.matchMedia?.('(pointer: coarse)').matches,
  };
}
